import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, ArrowRight, CheckCircle2 } from "lucide-react";
import { SectionLabel } from "@/components/Section";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REVENUE_OPTIONS = [
  "$2M – $5M",
  "$5M – $10M",
  "$10M – $15M",
  "$15M+",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    business_name: "",
    email: "",
    phone: "",
    revenue_range: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.revenue_range) {
      toast.error("Please select a revenue range.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Message received. We'll be in touch shortly.");
      setForm({
        name: "",
        business_name: "",
        email: "",
        phone: "",
        revenue_range: "",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="relative border-b border-[#E2E8F0] bg-white">
        <div className="bb-grid-bg absolute inset-0 opacity-60 pointer-events-none" />
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 pt-10 lg:pt-16 pb-20 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <SectionLabel>Contact</SectionLabel>
            <h1
              className="bb-fade-up mt-5 text-5xl sm:text-6xl lg:text-8xl font-extrabold tracking-tighter leading-[1] text-[#0A192F]"
              data-testid="contact-hero-title"
            >
              Let's Talk.
            </h1>
            <p className="bb-fade-up bb-delay-2 mt-8 text-lg lg:text-xl text-[#475569] leading-relaxed max-w-2xl">
              Tell us where the pressure points are. We'll come back within one business day.
            </p>

            <div className="mt-12 space-y-6">
              <a
                href="mailto:hello@backbench.co.nz"
                className="group flex items-center gap-4 text-lg lg:text-2xl font-bold text-[#0A192F] hover:text-[#F68712] transition-colors"
                data-testid="contact-email-link"
              >
                <Mail size={22} strokeWidth={1.75} />
                <span>hello@backbench.co.nz</span>
                <ArrowRight
                  size={18}
                  strokeWidth={2.25}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                />
              </a>
              <a
                href="tel:+64272315706"
                className="group flex items-center gap-4 text-lg lg:text-2xl font-bold text-[#0A192F] hover:text-[#F68712] transition-colors"
                data-testid="contact-phone-link"
              >
                <Phone size={22} strokeWidth={1.75} />
                <span>027 231 5706</span>
              </a>
            </div>
          </div>

          {/* FORM */}
          <div className="lg:col-span-5">
            <div className="bg-[#F8FAFC] border border-[#0A192F]/15 p-8 lg:p-10">
              {submitted ? (
                <div
                  className="flex flex-col items-start gap-5 py-8"
                  data-testid="contact-success-state"
                >
                  <CheckCircle2 size={36} strokeWidth={1.75} className="text-[#F68712]" />
                  <h3 className="text-3xl font-extrabold tracking-tighter text-[#0A192F]">
                    Message received.
                  </h3>
                  <p className="text-base text-[#475569] leading-relaxed">
                    Thanks — we'll be in touch within one business day.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-2 text-sm font-bold tracking-widest uppercase text-[#0A192F] hover:text-[#F68712] inline-flex items-center gap-2 transition-colors"
                    data-testid="contact-send-another-button"
                  >
                    Send another <ArrowRight size={14} strokeWidth={2.5} />
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="space-y-5"
                  data-testid="contact-form"
                  noValidate
                >
                  <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#F68712]">
                    Send a brief
                  </div>

                  <Field label="Name" testId="contact-field-name">
                    <input
                      required
                      value={form.name}
                      onChange={update("name")}
                      className="bb-input"
                      placeholder="Jane Smith"
                      data-testid="contact-input-name"
                      maxLength={120}
                    />
                  </Field>

                  <Field label="Business Name" testId="contact-field-business">
                    <input
                      required
                      value={form.business_name}
                      onChange={update("business_name")}
                      className="bb-input"
                      placeholder="Acme Plumbing Ltd"
                      data-testid="contact-input-business"
                      maxLength={200}
                    />
                  </Field>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Email" testId="contact-field-email">
                      <input
                        required
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        className="bb-input"
                        placeholder="you@business.co.nz"
                        data-testid="contact-input-email"
                      />
                    </Field>
                    <Field label="Phone" testId="contact-field-phone">
                      <input
                        required
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        className="bb-input"
                        placeholder="027 ..."
                        data-testid="contact-input-phone"
                        maxLength={40}
                      />
                    </Field>
                  </div>

                  <Field label="Revenue Range" testId="contact-field-revenue">
                    <select
                      required
                      value={form.revenue_range}
                      onChange={update("revenue_range")}
                      className="bb-input bg-white appearance-none cursor-pointer"
                      data-testid="contact-input-revenue"
                    >
                      <option value="" disabled>
                        Select range…
                      </option>
                      {REVENUE_OPTIONS.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Short Message" testId="contact-field-message">
                    <textarea
                      required
                      rows={5}
                      value={form.message}
                      onChange={update("message")}
                      className="bb-input resize-none"
                      placeholder="Where are the bottlenecks? What's broken?"
                      data-testid="contact-input-message"
                      maxLength={2000}
                    />
                  </Field>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#F68712] hover:bg-[#D87410] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold tracking-wide px-7 py-4 transition-colors duration-200 group"
                    data-testid="contact-submit-button"
                  >
                    {submitting ? "Sending…" : "Send Message"}
                    {!submitting && (
                      <ArrowRight
                        size={18}
                        strokeWidth={2.25}
                        className="transition-transform duration-200 group-hover:translate-x-1"
                      />
                    )}
                  </button>

                  <p className="text-xs text-[#475569] leading-relaxed">
                    By submitting you agree to be contacted about your enquiry. We don't share details.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Local input style helper */}
      <style>{`
        .bb-input {
          width: 100%;
          background: #FFFFFF;
          border: 1px solid #E2E8F0;
          padding: 14px 16px;
          color: #0A192F;
          font-family: 'Manrope', sans-serif;
          font-weight: 500;
          font-size: 15px;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
          border-radius: 0;
        }
        .bb-input::placeholder { color: #94A3B8; }
        .bb-input:focus {
          outline: none;
          border-color: #F68712;
          box-shadow: 0 0 0 1px #F68712;
        }
      `}</style>
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <span className="block text-xs font-bold uppercase tracking-[0.2em] text-[#0A192F]/70 mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
